/**
 * Sensor utility library for Trackizy advanced sensor configuration.
 *
 * Pure-JS helpers (parseSensorConfig, resolveColorInterval, colorStatusToHex,
 * SENSOR_TEMPLATES) moved to @trackizy/formatters so mobile can share them.
 * The icon-bound helpers (SENSOR_ICONS, resolveSensorIcon) stay here because
 * they embed lucide-react component references — per packages/CLAUDE.md rule
 * 1 ("no framework-specific imports"), the shared package must be
 * framework-free. Mobile can reimplement the icon mapping with
 * lucide-react-native if needed.
 */

import {
  Fuel,
  Thermometer,
  Zap,
  Gauge,
  Car,
  Droplets,
  Activity,
  Battery,
  Weight,
  DoorOpen,
  Wind,
  Cpu,
  Signal,
  AlertTriangle,
  BarChart2,
} from 'lucide-react';

// Pure helpers shared with mobile via @trackizy/formatters.
export {
  parseSensorConfig,
  resolveColorInterval,
  colorStatusToHex,
  SENSOR_TEMPLATES,
} from '@trackizy/formatters';

// ── SENSOR_ICONS ───────────────────────────────────────────────────────────────

/**
 * Curated icon map for sensor icon picker.
 * All imports are explicit to avoid tree-shaking issues (per CLAUDE.md).
 */
export const SENSOR_ICONS = [
  { key: 'Fuel',          icon: Fuel,          label: 'Fuel' },
  { key: 'Thermometer',   icon: Thermometer,   label: 'Temperature' },
  { key: 'Zap',           icon: Zap,           label: 'Voltage / Power' },
  { key: 'Gauge',         icon: Gauge,         label: 'Gauge / Speed' },
  { key: 'Car',           icon: Car,           label: 'Vehicle' },
  { key: 'Droplets',      icon: Droplets,      label: 'Liquid / Water' },
  { key: 'Activity',      icon: Activity,      label: 'Activity / Signal' },
  { key: 'Battery',       icon: Battery,       label: 'Battery' },
  { key: 'Weight',        icon: Weight,        label: 'Weight / Load' },
  { key: 'DoorOpen',      icon: DoorOpen,      label: 'Door' },
  { key: 'Wind',          icon: Wind,          label: 'Wind / Air' },
  { key: 'Cpu',           icon: Cpu,           label: 'CPU / Digital' },
  { key: 'Signal',        icon: Signal,        label: 'Signal / Network' },
  { key: 'AlertTriangle', icon: AlertTriangle, label: 'Alert' },
  { key: 'BarChart2',     icon: BarChart2,     label: 'Chart / Analytics' },
];

// ── resolveSensorIcon ──────────────────────────────────────────────────────────

/**
 * Lookup icon component from SENSOR_ICONS by key.
 * Returns Activity component as fallback.
 * @param {string} iconKey
 * @returns {React.ComponentType}
 */
export function resolveSensorIcon(iconKey) {
  const found = SENSOR_ICONS.find((s) => s.key === iconKey);
  return found ? found.icon : Activity;
}
